/**
 * Report dates, without the time.
 *
 * POPS returns some date columns as a plain "12/09/2026" and others as a full
 * timestamp - "2026-09-12T00:00:00", "2026-09-12 00:00:00.000",
 * "12/09/2026 12:00:00 AM". The report tables printed whatever came back, so a
 * few of them showed a midnight time next to every row.
 *
 * This trims the time part and leaves everything else exactly as the server
 * sent it, so a column that already reads "12/09/2026" is untouched. The one
 * rewrite is ISO "2026-09-12", which becomes "12-09-2026" to match the format
 * the rest of the app prints dates in.
 *
 * Anything that is not a string with a time on the end - a number, a blank, a
 * status word - comes back unchanged, so this is safe to run over a whole row
 * of mixed values.
 */

/* "2026-09-12T10:23", "... 10:23:00", "... 10:23:00.123", with an optional
   AM/PM and an optional +05:30 / Z offset. */
const TIME_TAIL =
    /^(.*?)[T\s]+\d{1,2}:\d{2}(?::\d{2})?(?:\.\d+)?\s*(?:[AaPp]\.?[Mm]\.?)?\s*(?:Z|[+-]\d{2}:?\d{2})?$/;

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

export const dateOnly = (value) => {
    if (value === null || value === undefined) {
        return value;
    }

    if (typeof value !== 'string') {
        return value;
    }

    const text = value.trim();

    if (!text) {
        return value;
    }

    const withoutTime = TIME_TAIL.exec(text);
    const datePart = withoutTime ? withoutTime[1].trim() : text;

    if (!datePart) {
        return value;
    }

    const iso = ISO_DATE.exec(datePart);

    if (iso) {
        return iso[3] + '-' + iso[2] + '-' + iso[1];
    }

    return datePart;
};

/* Case-insensitive field read. POPS spells the same column differently
   between endpoints - `orderdate` on one, `orderDate` on the next - and a
   miss there prints an empty cell with nothing to explain it. */
const lookup = (row, key) => {
    if (row[key] !== undefined) {
        return row[key];
    }

    const wanted = key.toLowerCase();
    const match = Object.keys(row).find((name) => name.toLowerCase() === wanted);

    return match ? row[match] : undefined;
};

/**
 * The date to print for a row: the first of `keys` the row actually carries,
 * matched whatever its capitalisation, with the time trimmed off.
 *
 * @param {object}    row
 * @param {...string} keys  field names to try, in order of preference
 */
export const dateField = (row, ...keys) => {
    if (!row) {
        return '';
    }

    for (let i = 0; i < keys.length; i += 1) {
        const value = lookup(row, keys[i]);

        if (value !== undefined && value !== null && String(value).trim() !== '') {
            return dateOnly(value);
        }
    }

    return '';
};

export default dateOnly;
